import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { ModalService } from '@base/services/private/modal.service';
import { ReqLabelParams } from '@base/models/requests/req-label.req';
import { TypeOfOperation, TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { MeetService } from '@base/services/private/meet.service';
import { IdParams } from '@base/models/requests/requests.req';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { GlobalService } from '@base/services/private/global.service';
import { finalize, Subscription } from 'rxjs';
import { SkeletonGeneralListComponent } from '@base/components/private/skeleton/skeleton-general-list/skeleton-general-list.component';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ThemeService } from '@base/services/private/theme.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { TypeOfDecline } from '@base/enums/common.enum';

// local enum for active button states
enum ActiveButton {
  UNDECLINE_MEETING = 'undecline-meeting',
}
@Component({
  selector: 'app-modal-undecline-request',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    forwardRef(() => TranslatePipe),
    SkeletonGeneralListComponent,
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-undecline-request.component.html',
  styleUrl: './modal-undecline-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalUndeclineRequestComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly meetSrv = inject(MeetService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalSrv = inject(GlobalService);
  private readonly themeSrv = inject(ThemeService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly translatePipe = inject(TranslatePipe);

  @Input() modalId = '';
  @Input() peopleId = '';
  @Input() reason = '';
  @Input() action: TypeOfRequestLabel | string = '';

  activeButton = '';
  httpRequestObserver$: Subscription | null = null;

  isBgDarkMode = this.themeSrv.isBgDarkMode;
  isHttpRequestRunning = false;
  isShowPlaceholder = true;
  isExternalMode = this.globalStateSrv.isExternalMode();

  ActiveButton = ActiveButton;

  /**
   * Lifecycle hook that initializes the component and fetches the reason.
   * @returns void
   */
  ngOnInit(): void {
    if (!this.reason) {
      this.getReason();
    }
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Closes the current modal by its ID.
   * @returns void
   */
  close(): void {
    this.modalSrv.close();
  }

  /**
   * Fetches the reason for interaction based on peopleId.
   * @returns void
   */
  getReason(): void {
    const params: IdParams = {
      id: this.peopleId,
    };
    this.meetSrv.getInteractionReason(params).subscribe({
      next: (res) => {
        this.reason = res.reason;
        this.isShowPlaceholder = false;
        this.triggerChangeDetection();
      },
      error: (err) => {
        this.isShowPlaceholder = false;
        console.error('Error fetching interaction reason:', err);
      },
    });
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends a request to undecline the handshake and updates the label.
   * @returns void
   */
  undeclineReq(): void {
    const params: ReqLabelParams = {
      id_object: this.peopleId,
      action: TypeOfRequestLabel.DECLINE_HANDSHAKE,
      operation: TypeOfOperation.NEGATIVE,
      reason: '',
    };

    this.activeButton = ActiveButton.UNDECLINE_MEETING;
    this.requestLabelSrv
      .updateLabel(params)
      .pipe(
        finalize(() => {
          this.activeButton = '';
        })
      )
      .subscribe({
        next: (res) => {
          if (res.status === StatusResponse.TEXT_SUCCESS) {
            if (this.isExternalMode) {
              this.globalSrv.postMessageToTargets({
                is_success: true,
                message: this.translatePipe.transform('DECLINED_REMOVED'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
                isShowModal: true,
              });
              return;
            }

            this.close();

            const data = {
              status: true,
              data: {
                id: this.peopleId,
                action: this.action,
                newValue: {
                  declined: TypeOfDecline.NO_DECLINE,
                },
              },
            };
            this.requestLabelSrv.setEventUpdated(data);
          } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
            // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
            this.showModalWarning();
          }
        },
        error: (err) => {
          console.error('Error undeclining request:', err);
        },
      });
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
